import angular from 'angular';
import { NsjRoutingType } from '@core/nsj-core';
import {
    IBureau,
    IBureauMembro,
    IBureauOffset,
    IBureauTenant,
} from './models/bureaus.model';

export class MeurhBureausService {
    static $inject = ['$http', 'nsjRouting', '$rootScope', '$q'];

    public listBusy = false;
    public loading_deferred: angular.IDeferred<unknown> | null = null;

    constructor(
        public $http: angular.IHttpService,
        public nsjRouting: NsjRoutingType,
        public $rootScope: angular.IRootScopeService,
        public $q: angular.IQService,
    ) { }

    /** Sem `offset`, a primeira página; com ele, a página seguinte ao Bureau informado. */
    public getList(filtro: string = '', offset: IBureauOffset | null = null): angular.IPromise<IBureau[]> {
        if (this.loading_deferred) {
            this.loading_deferred.resolve();
        }

        const deferred = this.$q.defer();
        this.loading_deferred = deferred;
        this.listBusy = true;

        const params: { filter?: string, offset?: IBureauOffset } = {};

        if (filtro) {
            params.filter = filtro;
        }

        if (offset) {
            params.offset = offset;
        }

        return this.$q<IBureau[]>((resolve, reject) => {
            this.$http
                .get<IBureau[]>(
                    this.nsjRouting.generate('bureaus_index', params, true),
                    { timeout: deferred.promise }
                )
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                })
                .finally(() => {
                    // a requisição cancelada por uma nova não desliga o busy da nova
                    if (this.loading_deferred === deferred) {
                        this.listBusy = false;
                    }
                });
        });
    }

    public create(nome: string): angular.IPromise<{ bureau: string }> {
        return this.$q<{ bureau: string }>((resolve, reject) => {
            this.$http
                .post<{ bureau: string }>(
                    this.nsjRouting.generate('bureaus_create', {}, true),
                    { nome }
                )
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public update(bureau: string, nome: string): angular.IPromise<void> {
        return this.$q<void>((resolve, reject) => {
            this.$http
                .put<void>(
                    this.nsjRouting.generate('bureaus_put', { id: bureau }, true),
                    { nome }
                )
                .then(() => {
                    resolve();
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public delete(bureau: string): angular.IPromise<void> {
        return this.$q<void>((resolve, reject) => {
            this.$http
                .delete<void>(
                    this.nsjRouting.generate('bureaus_delete', { id: bureau }, true)
                )
                .then(() => {
                    resolve();
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public getTenants(bureau: string): angular.IPromise<IBureauTenant[]> {
        return this.$q<IBureauTenant[]>((resolve, reject) => {
            this.$http
                .get<IBureauTenant[]>(
                    this.nsjRouting.generate('bureaus_index_tenants', { id: bureau }, true)
                )
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    /** `tenantId` chega já convertido: o backend valida com `is_int()` e recusa string. */
    public createTenant(bureau: string, tenantId: number): angular.IPromise<{ bureautenant: string }> {
        return this.$q<{ bureautenant: string }>((resolve, reject) => {
            this.$http
                .post<{ bureautenant: string }>(
                    this.nsjRouting.generate('bureaus_create_tenant', { id: bureau }, true),
                    { tenant_id: tenantId }
                )
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public deleteTenant(bureau: string, bureautenant: string): angular.IPromise<void> {
        return this.$q<void>((resolve, reject) => {
            this.$http
                .delete<void>(
                    this.nsjRouting.generate('bureaus_delete_tenant', { id: bureau, bureautenant }, true)
                )
                .then(() => {
                    resolve();
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public getMembros(bureau: string): angular.IPromise<IBureauMembro[]> {
        return this.$q<IBureauMembro[]>((resolve, reject) => {
            this.$http
                .get<IBureauMembro[]>(
                    this.nsjRouting.generate('bureaus_index_membros', { id: bureau }, true)
                )
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public createMembro(bureau: string, email: string): angular.IPromise<{ bureaumembro: string }> {
        return this.$q<{ bureaumembro: string }>((resolve, reject) => {
            this.$http
                .post<{ bureaumembro: string }>(
                    this.nsjRouting.generate('bureaus_create_membro', { id: bureau }, true),
                    { email }
                )
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public deleteMembro(bureau: string, bureaumembro: string): angular.IPromise<void> {
        return this.$q<void>((resolve, reject) => {
            this.$http
                .delete<void>(
                    this.nsjRouting.generate('bureaus_delete_membro', { id: bureau, bureaumembro }, true)
                )
                .then(() => {
                    resolve();
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }
}
